<template>
  <span class="v-avatar" :style>
    <img
      v-if="imageVisible"
      alt=""
      :loading
      :src
      :style="{ visibility: loaded ? 'visible' : 'hidden', width: `${props.size}px`, height: `${props.size}px` }"
      @error="onError"
      @load="loaded = true"
    />

    <span v-if="!loaded" style="position: absolute">
      <template v-if="placeholder">{{ placeholder }}</template>
      <template v-else>&nbsp;</template>
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { stringToHue } from '../utils/helpers.ts'

interface Props {
  src?: string
  size?: number
  placeholder?: string
  seed?: string | number
  color?: string
  loading?: 'lazy' | 'eager'
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  size: 26,
  placeholder: '',
  seed: '',
  color: undefined,
  loading: 'lazy',
})

const emit = defineEmits<{
  error: [Event]
}>()

const hasError = ref(false)
const loaded = ref(false)

const imageVisible = computed(() => props.src && !hasError.value)

const backgroundColor = computed(() => {
  const color = '#cccccc'

  if (!hasError.value && props.src) return color
  if (props.color) return props.color

  if (props.placeholder) {
    const hue = stringToHue(`${props.seed}${props.placeholder}`)

    return `hsl(${hue}, 60%, 50%)`
  }

  return color
})

const style = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  borderRadius: `${props.size * 2}px`,
  backgroundColor: backgroundColor.value,
}))

watch(
  () => props.src,
  () => {
    loaded.value = false
    hasError.value = false
  },
)

function onError(error: Event) {
  hasError.value = true
  emit('error', error)
}
</script>

<style>
.v-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  overflow: hidden;
  flex-shrink: 0;
  vertical-align: middle;
  position: relative;
}

.v-avatar > img {
  object-fit: cover;
  max-width: none;
}
</style>
